import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useI18n } from "@/i18n/I18nContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Plus, Trash2, Star, MessageCircle, AlertTriangle } from "lucide-react";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";
import {
  PANAMA_TZ, toPanamaParts, durationOf,
  ClassTypesSection, SessionFormDialog, SessionEditDialog, ClassTypeDialog, DuplicateWeekDialog, DeleteWeekDialog,
} from "@/components/admin/scheduleParts";

function CoachAccountsSection() {
  const { tl } = useI18n();
  const [coaches, setCoaches] = useState([]);
  const [emails, setEmails] = useState({});
  const load = () => api.get("/admin/coaches").then(({ data }) => setCoaches(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const link = async (coach_id) => {
    const email = (emails[coach_id] || "").trim();
    if (!email) return;
    try {
      const { data } = await api.post(`/admin/coaches/${coach_id}/link-account`, { email });
      toast.success(tl({ es: `Cuenta vinculada: ${data.email}`, en: `Account linked: ${data.email}`, fr: `Compte relié : ${data.email}` }));
      setEmails((p) => ({ ...p, [coach_id]: "" }));
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d === "account_not_found"
        ? tl({ es: "No existe una cuenta con ese correo. El coach debe registrarse primero.", en: "No account with that email. The coach must register first.", fr: "Aucun compte avec cet email. Le coach doit d'abord créer un compte." })
        : formatApiError(d));
    }
  };
  const unlink = async (coach_id) => {
    if (!window.confirm(tl({ es: "¿Desvincular la cuenta de este coach?", en: "Unlink this coach's account?", fr: "Dissocier le compte de ce coach ?" }))) return;
    try { await api.post(`/admin/coaches/${coach_id}/unlink-account`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="mb-8 rounded-2xl border border-border bg-card p-6" data-testid="coach-accounts">
      <h2 className="mb-1 font-display text-2xl font-bold tracking-tight">{tl({ es: "Coaches y cuentas", en: "Coaches & accounts", fr: "Coachs & comptes" })}</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        {tl({ es: "Vincula la cuenta de cada coach para que vea sus clases y quién está inscrito.",
              en: "Link each coach's account so they can see their classes and who is enrolled.",
              fr: "Relie le compte de chaque coach pour qu'il voie ses cours et qui est inscrit." })}
      </p>
      <div className="space-y-2">
        {coaches.map((c) => {
          const needsLink = !c.user_id && c.upcoming_sessions > 0;
          return (
          <div key={c.coach_id} data-testid={`coach-acct-${c.coach_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-secondary/30 px-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-medium">
                {c.name}
                {needsLink && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-500"><AlertTriangle className="h-3 w-3" />{tl({ es: "Sin cuenta", en: "No account", fr: "Aucun compte" })}</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {c.upcoming_sessions} {tl({ es: "clases próximas", en: "upcoming classes", fr: "cours à venir" })}
                {c.account_email ? ` · ${c.account_email}` : ""}
              </div>
            </div>
            {c.user_id ? (
              <Button data-testid={`coach-unlink-${c.coach_id}`} size="sm" variant="outline" onClick={() => unlink(c.coach_id)} className="rounded-full text-destructive">
                {tl({ es: "Desvincular", en: "Unlink", fr: "Dissocier" })}
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input data-testid={`coach-email-${c.coach_id}`} type="email" placeholder="email@..." value={emails[c.coach_id] || ""}
                  onChange={(e) => setEmails((p) => ({ ...p, [c.coach_id]: e.target.value }))} className="h-9 w-56" />
                <Button data-testid={`coach-link-${c.coach_id}`} size="sm" onClick={() => link(c.coach_id)} className="rounded-full">
                  {tl({ es: "Vincular", en: "Link", fr: "Relier" })}
                </Button>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}


export function AdminClasses() {
  const { t, tl, lang } = useI18n();
  const [classTypes, setClassTypes] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [clients, setClients] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const bump = () => setReloadKey((k) => k + 1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ class_type_id: "", universe: "fitness", coach_id: "", room_id: "", date: "", time: "07:00", duration: 60, capacity: 12, recurring_weeks: 0 });
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [ctOpen, setCtOpen] = useState(false);
  const [ct, setCt] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [dup, setDup] = useState({ source_date: new Date().toLocaleDateString("en-CA", { timeZone: PANAMA_TZ }), weeks: 4 });
  const [delOpen, setDelOpen] = useState(false);
  const [del, setDel] = useState({ source_date: new Date().toLocaleDateString("en-CA", { timeZone: PANAMA_TZ }) });

  const loadCts = () => api.get("/class-types").then(({ data }) => setClassTypes(data));
  useEffect(() => {
    loadCts();
    api.get("/coaches").then(({ data }) => setCoaches(data));
    api.get("/rooms").then(({ data }) => setRooms(data));
    api.get("/admin/clients").then(({ data }) => setClients(data));
  }, []);

  const create = async () => {
    try {
      const start = new Date(`${form.date}T${form.time}:00-05:00`);
      const end = new Date(start.getTime() + form.duration * 60000);
      await api.post("/admin/sessions", {
        class_type_id: form.class_type_id, universe: form.universe, coach_id: form.coach_id || null,
        room_id: form.room_id || null, start: start.toISOString(), end: end.toISOString(),
        capacity: Number(form.capacity), level: "all", recurring_weeks: Number(form.recurring_weeks),
      });
      toast.success(t("common.success"));
      setOpen(false); bump();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    try { const { data } = await api.post(`/admin/sessions/${id}/cancel`); toast.success(`${t("common.success")} (${data.refunded} ↩)`); setEditOpen(false); bump(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (s) => {
    const { date, time } = toPanamaParts(s.start);
    setEdit({ session_id: s.session_id, class_type_id: s.class_type_id, coach_id: s.coach_id || "", room_id: s.room_id || "", date, time, duration: durationOf(s), capacity: s.capacity, booked: s.booked_count });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      const start = new Date(`${edit.date}T${edit.time}:00-05:00`);
      const end = new Date(start.getTime() + edit.duration * 60000);
      await api.put(`/admin/sessions/${edit.session_id}`, {
        class_type_id: edit.class_type_id, coach_id: edit.coach_id || null, room_id: edit.room_id || null,
        start: start.toISOString(), end: end.toISOString(), capacity: Number(edit.capacity),
      });
      toast.success(t("common.success")); setEditOpen(false); bump();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openCt = (c) => {
    if (c) setCt({ class_type_id: c.class_type_id, name_es: c.name?.es || "", name_en: c.name?.en || "", name_fr: c.name?.fr || "", universe: c.universe || "fitness", duration_min: c.duration_min || 60, capacity: c.capacity || 12, credit_type: c.credit_type || "fitness", cancel_hours: c.cancel_hours ?? "" });
    else setCt({ class_type_id: "", name_es: "", name_en: "", name_fr: "", universe: "fitness", duration_min: 60, capacity: 12, credit_type: "fitness", cancel_hours: "" });
    setCtOpen(true);
  };
  const saveCt = async () => {
    try {
      const body = {
        name: { es: ct.name_es, en: ct.name_en || ct.name_es, fr: ct.name_fr || ct.name_es },
        universe: ct.universe, duration_min: Number(ct.duration_min), capacity: Number(ct.capacity),
        credit_type: ct.credit_type,
        cancel_hours: ct.cancel_hours === "" || ct.cancel_hours == null ? null : Number(ct.cancel_hours),
      };
      if (ct.class_type_id) body.class_type_id = ct.class_type_id;
      await api.post("/admin/class-types", body);
      toast.success(t("common.success")); setCtOpen(false); loadCts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const duplicateWeek = async () => {
    try {
      const { data } = await api.post("/admin/sessions/duplicate-week", { source_date: dup.source_date, weeks: Number(dup.weeks) });
      toast.success(tl({ es: `${data.created} clases creadas`, en: `${data.created} classes created`, fr: `${data.created} cours créés` }));
      setDupOpen(false); bump();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteWeek = async () => {
    try {
      const { data } = await api.post("/admin/sessions/delete-week", { source_date: del.source_date });
      toast.success(tl({ es: `${data.deleted} clases eliminadas`, en: `${data.deleted} classes deleted`, fr: `${data.deleted} cours supprimés` }));
      setDelOpen(false); bump();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <ClassTypesSection classTypes={classTypes} onNew={() => openCt(null)} onEdit={openCt} />

      <CoachAccountsSection />

      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold tracking-tight">{tl({ es: "Horario", en: "Schedule", fr: "Planning" })}</h2>
        <div className="flex items-center gap-2">
          <Button data-testid="delete-week-btn" onClick={() => setDelOpen(true)} variant="outline" className="rounded-full text-destructive"><Trash2 className="mr-1 h-4 w-4" />{tl({ es: "Eliminar semana", en: "Delete week", fr: "Supprimer la semaine" })}</Button>
          <Button data-testid="duplicate-week-btn" onClick={() => setDupOpen(true)} variant="outline" className="rounded-full"><Plus className="mr-1 h-4 w-4" />{tl({ es: "Duplicar semana", en: "Duplicate week", fr: "Dupliquer la semaine" })}</Button>
          <Button data-testid="new-session-btn" onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1 h-4 w-4" />{t("admin.newSession")}</Button>
        </div>
      </div>

      <ScheduleCalendar onSessionClick={openEdit} reloadKey={reloadKey} bookingBadges={false} showCounts={true} />

      <SessionFormDialog open={open} setOpen={setOpen} form={form} setForm={setForm} classTypes={classTypes} coaches={coaches} rooms={rooms} onCreate={create} />
      <SessionEditDialog open={editOpen} setOpen={setEditOpen} edit={edit} setEdit={setEdit} classTypes={classTypes} coaches={coaches} onSave={saveEdit} onCancel={cancel} clients={clients} onChanged={bump} />
      <ClassTypeDialog open={ctOpen} setOpen={setCtOpen} ct={ct} setCt={setCt} onSave={saveCt} />
      <DuplicateWeekDialog open={dupOpen} setOpen={setDupOpen} dup={dup} setDup={setDup} onConfirm={duplicateWeek} />
      <DeleteWeekDialog open={delOpen} setOpen={setDelOpen} del={del} setDel={setDel} onConfirm={deleteWeek} />
    </AdminLayout>
  );
}

export function AdminProducts() {
  const { t, tl } = useI18n();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name_es: "", name_en: "", name_fr: "", category: "pack", price: 100, credits: 10, scope: "all", validity_days: 60 });

  const load = () => api.get("/products?active_only=false").then(({ data }) => setProducts(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/products", {
        name: { es: f.name_es, en: f.name_en || f.name_es, fr: f.name_fr || f.name_es },
        description: { es: "", en: "", fr: "" }, category: f.category, price: Number(f.price), tax: 0,
        credits: Number(f.credits), service_scope: f.scope === "all" ? ["all"] : [f.scope],
        validity_days: Number(f.validity_days), auto_renew: false, active: true,
      });
      toast.success(t("common.success")); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggle = async (p) => {
    await api.post("/admin/products", { ...p, active: !p.active });
    load();
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("admin.products")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-product-btn" className="rounded-full"><Plus className="mr-1 h-4 w-4" />+</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("admin.products")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre (ES)</Label><Input data-testid="product-name-es" value={f.name_es} onChange={(e) => setF({ ...f, name_es: e.target.value })} className="mt-1.5 h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre (EN)</Label><Input data-testid="product-name-en" value={f.name_en} onChange={(e) => setF({ ...f, name_en: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label>Nom (FR)</Label><Input data-testid="product-name-fr" value={f.name_fr} onChange={(e) => setF({ ...f, name_fr: e.target.value })} className="mt-1.5 h-11" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Precio</Label><Input data-testid="product-price" type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="mt-1.5 h-11" /></div>
                <div><Label>Créditos</Label><Input data-testid="product-credits" type="number" value={f.credits} onChange={(e) => setF({ ...f, credits: e.target.value })} className="mt-1.5 h-11" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Válido</Label>
                  <select data-testid="product-scope" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} className="mt-1.5 h-11 w-full rounded-lg border border-border px-3">
                    <option value="all">Fitness + Pilates</option><option value="fitness">Fitness</option><option value="pilates">Pilates</option>
                  </select>
                </div>
                <div><Label>Validez (días)</Label><Input data-testid="product-validity" type="number" value={f.validity_days} onChange={(e) => setF({ ...f, validity_days: e.target.value })} className="mt-1.5 h-11" /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="create-product-btn" onClick={create} disabled={!f.name_es} className="rounded-full">{t("common.confirm")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((p) => (
          <div key={p.product_id} data-testid={`admin-product-${p.product_id}`} className={`rounded-2xl border border-border bg-card p-5 ${!p.active && "opacity-50"}`}>
            <div className="font-display text-lg font-semibold">{tl(p.name)}</div>
            <div className="text-sm text-muted-foreground">${p.price} · {p.credits >= 999 ? "∞" : p.credits} cr · {(p.service_scope || []).join(",")}</div>
            <Button size="sm" variant="outline" data-testid={`toggle-product-${p.product_id}`} onClick={() => toggle(p)} className="mt-3 rounded-full">{p.active ? "Desactivar" : "Activar"}</Button>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

// Module-scope field component so inputs don't remount (and lose focus) on each keystroke.
function SettingField({ label, path, type = "text", value, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} data-testid={`setting-${path.replace(/\./g, "-")}`} value={value ?? ""}
        onChange={(e) => onChange(path, type === "number" ? Number(e.target.value) : e.target.value)} className="mt-1.5 h-11" />
    </div>
  );
}

function ReviewWallManager() {
  const [reviews, setReviews] = useState([]);
  const [f, setF] = useState({ name: "", rating: 5, text: "" });
  const load = () => api.get("/reviews").then(({ data }) => setReviews(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name.trim() || !f.text.trim()) { toast.error("Nombre y texto requeridos"); return; }
    try { await api.post("/admin/reviews", { name: f.name, rating: Number(f.rating), text: f.text }); setF({ name: "", rating: 5, text: "" }); await load(); toast.success("Reseña añadida"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/reviews/${id}`); await load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  return (
    <section className="rounded-2xl border border-border bg-card p-6" data-testid="review-wall-manager">
      <h2 className="mb-1 font-display font-semibold">Muro de reseñas (portada)</h2>
      <p className="mb-4 text-xs text-muted-foreground">Añade tus mejores reseñas de Google. Se muestran en la página de inicio.</p>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
            <div><Label>Nombre</Label><Input data-testid="review-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 h-11" placeholder="Ana G." /></div>
            <div><Label>Nota (1-5)</Label><Input data-testid="review-rating" type="number" min="1" max="5" value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })} className="mt-1.5 h-11" /></div>
          </div>
          <div><Label>Reseña</Label><Input data-testid="review-text" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className="mt-1.5 h-11" placeholder="El mejor gym de Coronado..." /></div>
        </div>
        <Button data-testid="review-add-btn" onClick={add} className="h-11 rounded-full"><Plus className="mr-1 h-4 w-4" />Añadir</Button>
      </div>
      <div className="mt-4 space-y-2">
        {reviews.map((r) => (
          <div key={r.review_id} data-testid={`review-row-${r.review_id}`} className="flex items-start justify-between gap-3 rounded-lg bg-secondary/40 px-3 py-2 text-sm">
            <div className="min-w-0">
              <div className="flex items-center gap-2"><span className="font-semibold">{r.name}</span><span className="flex gap-0.5">{[...Array(r.rating || 5)].map((_, j) => <Star key={j} className="h-3 w-3 fill-primary text-primary" />)}</span></div>
              <p className="truncate text-muted-foreground">{r.text}</p>
            </div>
            <button data-testid={`review-del-${r.review_id}`} onClick={() => del(r.review_id)} className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-xs text-muted-foreground">Aún no hay reseñas.</p>}
      </div>
    </section>
  );
}

function WhatsAppReviewPanel({ reviewLink }) {
  const [cands, setCands] = useState([]);
  const load = () => api.get("/admin/review-candidates").then(({ data }) => setCands(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const msg = (name) => encodeURIComponent(`¡Hola ${name}! 🌟 Esperamos que disfrutes tus clases en Frenchies. ¿Nos dejas una reseña en Google? Solo toma 30 segundos: ${reviewLink || ""} ¡Gracias! 💪`);
  const send = async (c) => {
    const digits = (c.phone || "").replace(/\D/g, "");
    if (!digits) { toast.error("Este cliente no tiene teléfono"); return; }
    window.open(`https://wa.me/${digits}?text=${msg(c.name.split(" ")[0] || c.name)}`, "_blank");
    try { await api.post(`/admin/clients/${c.user_id}/mark-review-requested`); await load(); }
    catch (err) { if (process.env.NODE_ENV !== "production") console.error("mark-review-requested failed:", err); }
  };
  return (
    <section className="rounded-2xl border border-border bg-card p-6" data-testid="whatsapp-review-panel">
      <h2 className="mb-1 font-display font-semibold">Solicitar reseñas por WhatsApp</h2>
      <p className="mb-4 text-xs text-muted-foreground">Clientes que ya asistieron a suficientes clases. Un clic abre WhatsApp con el mensaje listo.</p>
      <div className="space-y-2">
        {cands.map((c) => (
          <div key={c.user_id} data-testid={`wa-cand-${c.user_id}`} className="flex items-center justify-between gap-3 rounded-lg bg-secondary/40 px-3 py-2 text-sm">
            <div className="min-w-0">
              <span className="font-medium">{c.name || "—"}</span>
              <span className="ml-2 text-xs text-muted-foreground">{c.attended} clases{c.review_requested ? " · ya solicitada" : ""}</span>
            </div>
            <Button data-testid={`wa-send-${c.user_id}`} size="sm" disabled={!c.phone} onClick={() => send(c)}
              className="h-8 shrink-0 rounded-full bg-[#25D366] px-3 text-xs text-white hover:bg-[#1ebe57]">
              <MessageCircle className="mr-1 h-3.5 w-3.5" />{c.phone ? "WhatsApp" : "Sin tel."}
            </Button>
          </div>
        ))}
        {cands.length === 0 && <p className="text-xs text-muted-foreground">Ningún cliente ha alcanzado el umbral todavía.</p>}
      </div>
    </section>
  );
}

export function AdminSettings() {
  const { t } = useI18n();
  const { settings, reload } = useSettings();
  const [s, setS] = useState(null);
  useEffect(() => { if (settings) setS(JSON.parse(JSON.stringify(settings))); }, [settings]);

  if (!s) return <AdminLayout><div className="py-16 text-center text-muted-foreground">{t("common.loading")}</div></AdminLayout>;

  const save = async () => {
    try { await api.put("/admin/settings", { settings: s }); await reload(); toast.success(t("common.success")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const upd = (path, val) => {
    const copy = { ...s };
    const parts = path.split(".");
    let node = copy;
    for (let i = 0; i < parts.length - 1; i++) { node[parts[i]] = { ...node[parts[i]] }; node = node[parts[i]]; }
    node[parts[parts.length - 1]] = val;
    setS(copy);
  };
  const val = (path) => path.split(".").reduce((a, k) => a?.[k], s);

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold tracking-tight">{t("admin.settings")}</h1>
        <Button data-testid="save-settings-btn" onClick={save} className="rounded-full">{t("common.save")}</Button>
      </div>
      <div className="grid max-w-3xl gap-6">
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold">Marca & contacto</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <SettingField label="Marca" path="brand" value={val("brand")} onChange={upd} />
            <SettingField label="WhatsApp (número)" path="whatsapp" value={val("whatsapp")} onChange={upd} />
            <SettingField label="Email" path="contact.email" value={val("contact.email")} onChange={upd} />
            <SettingField label="Email notif. nuevos registros" path="notify_email" value={val("notify_email")} onChange={upd} />
            <SettingField label="Teléfono" path="contact.phone" value={val("contact.phone")} onChange={upd} />
            <SettingField label="Dirección Fitness" path="contact.address_fitness" value={val("contact.address_fitness")} onChange={upd} />
            <SettingField label="Dirección Pilates" path="contact.address_pilates" value={val("contact.address_pilates")} onChange={upd} />
            <SettingField label="Instagram" path="social.instagram" value={val("social.instagram")} onChange={upd} />
            <SettingField label="Facebook" path="social.facebook" value={val("social.facebook")} onChange={upd} />
          </div>
        </section>
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-1 font-display font-semibold">Reseñas Google</h2>
          <p className="mb-4 text-xs text-muted-foreground">Enlace para dejar reseña, mapa y email automático de solicitud de reseña tras N clases asistidas.</p>
          <div className="grid gap-4">
            <SettingField label="Enlace reseña Google" path="google_review_url" value={val("google_review_url")} onChange={upd} />
            <SettingField label="Enlace Google Maps (opcional)" path="google_maps_url" value={val("google_maps_url")} onChange={upd} />
            <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
              <SettingField label="Google Place ID (auto-detectado si vacío)" path="google_place_id" value={val("google_place_id")} onChange={upd} />
              <Button type="button" variant="outline" data-testid="google-reviews-refresh-btn" className="rounded-full" onClick={async () => {
                try { const { data } = await api.post("/admin/google-reviews/refresh"); toast.success(`Google: ${data.rating} ★ · ${data.count} reseñas · ${data.reviews} importadas`); }
                catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}>Sincronizar reseñas Google</Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <SettingField label="Nota media (ej. 4.8)" path="review_rating" value={val("review_rating")} onChange={upd} />
              <SettingField label="N° de reseñas (ej. 28)" path="review_count" type="number" value={val("review_count")} onChange={upd} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <SettingField label="Pedir reseña tras (clases)" path="review_request_after" type="number" value={val("review_request_after")} onChange={upd} />
              <div>
                <label className="text-sm font-medium">Email automático de reseña</label>
                <select data-testid="setting-review-enabled" value={val("review_requests_enabled") === false ? "off" : "on"}
                  onChange={(e) => upd("review_requests_enabled", e.target.value === "on")}
                  className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3">
                  <option value="on">Activado</option>
                  <option value="off">Desactivado</option>
                </select>
              </div>
            </div>
          </div>
        </section>
        <ReviewWallManager />
        <WhatsAppReviewPanel reviewLink={val("google_review_url")} />
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold">Colores</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <SettingField label="Fitness primary" path="colors.fitness_primary" value={val("colors.fitness_primary")} onChange={upd} />
            <SettingField label="Pilates primary" path="colors.pilates_primary" value={val("colors.pilates_primary")} onChange={upd} />
          </div>
        </section>
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-1 font-display font-semibold">Reglas de reserva</h2>
          <p className="mb-4 text-xs text-muted-foreground">Apertura de inscripciones (días antes) y cierre (minutos antes del inicio, 0 = hasta el último minuto).</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <SettingField label="Fitness — abre (días antes)" path="booking_policy.fitness_open_days" type="number" value={val("booking_policy.fitness_open_days")} onChange={upd} />
            <SettingField label="Pilates — abre (días antes)" path="booking_policy.pilates_open_days" type="number" value={val("booking_policy.pilates_open_days")} onChange={upd} />
            <SettingField label="Privado — abre (días antes)" path="booking_policy.private_open_days" type="number" value={val("booking_policy.private_open_days")} onChange={upd} />
            <SettingField label="Fitness — cierra (min antes)" path="booking_policy.fitness_close_minutes" type="number" value={val("booking_policy.fitness_close_minutes")} onChange={upd} />
            <SettingField label="Pilates — cierra (min antes)" path="booking_policy.pilates_close_minutes" type="number" value={val("booking_policy.pilates_close_minutes")} onChange={upd} />
            <SettingField label="Privado — cierra (min antes)" path="booking_policy.private_close_minutes" type="number" value={val("booking_policy.private_close_minutes")} onChange={upd} />
          </div>
        </section>
        <section className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold">Política de cancelación (horas)</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            <SettingField label="Fitness" path="cancellation_policy.fitness_hours" type="number" value={val("cancellation_policy.fitness_hours")} onChange={upd} />
            <SettingField label="Pilates" path="cancellation_policy.pilates_hours" type="number" value={val("cancellation_policy.pilates_hours")} onChange={upd} />
            <SettingField label="Privado" path="cancellation_policy.private_hours" type="number" value={val("cancellation_policy.private_hours")} onChange={upd} />
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
